import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { Plus, X, Pencil, Check, ChevronDown, ChevronUp } from 'lucide-react';

function stringifyValue(val) {
  if (val === undefined || val === null) return '';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'object') return '';
  return String(val);
}

// Compare options arrays and return human-readable changes
function compareOptions(oldOptions, newOptions) {
  if (!oldOptions && !newOptions) return [];
  const oldList = Array.isArray(oldOptions) ? oldOptions : [];
  const newList = Array.isArray(newOptions) ? newOptions : [];
  const changes = [];
  const oldMap = new Map(oldList.map(o => [o.value, o]));
  const newMap = new Map(newList.map(o => [o.value, o]));

  for (const [value, newOpt] of newMap) {
    const oldOpt = oldMap.get(value);
    if (!oldOpt) {
      changes.push({ property: `option "${newOpt.label || value}"`, oldValue: '', newValue: 'added' });
    } else if (oldOpt.label !== newOpt.label) {
      changes.push({ property: `option "${value}"`, oldValue: oldOpt.label, newValue: newOpt.label });
    }
  }
  for (const [value, oldOpt] of oldMap) {
    if (!newMap.has(value)) {
      changes.push({ property: `option "${oldOpt.label || value}"`, oldValue: 'removed', newValue: '' });
    }
  }
  return changes;
}

// Compare configuration objects and return human-readable changes
function compareConfiguration(oldConfig, newConfig) {
  if (!oldConfig && !newConfig) return [];
  const old = oldConfig || {};
  const updated = newConfig || {};
  const changes = [];

  // Compare options specifically
  const optionChanges = compareOptions(old.options, updated.options);
  changes.push(...optionChanges);

  // Compare other scalar configuration properties
  const skipKeys = ['options'];
  const allKeys = new Set([...Object.keys(old), ...Object.keys(updated)]);
  for (const key of allKeys) {
    if (skipKeys.includes(key)) continue;
    const oldVal = stringifyValue(old[key]);
    const newVal = stringifyValue(updated[key]);
    if (oldVal !== newVal && (oldVal || newVal)) {
      changes.push({ property: key, oldValue: oldVal, newValue: newVal });
    }
  }

  return changes;
}

function computeFieldDiffs(original, updated) {
  const diffs = [];
  const originalMap = new Map();
  const matchedOriginalKeys = new Set();

  for (const f of original) {
    originalMap.set(f.key, f);
  }

  for (const updatedField of updated) {
    const matchKey = updatedField.key;
    const orig = originalMap.get(matchKey);

    if (!orig) {
      diffs.push({ field: updatedField, status: 'added', changes: [] });
      continue;
    }

    matchedOriginalKeys.add(matchKey);
    const changes = [];
    const propsToCompare = ['title', 'placeholder', 'description', 'required', 'type'];

    for (const prop of propsToCompare) {
      const oldVal = stringifyValue(orig[prop]);
      const newVal = stringifyValue(updatedField[prop]);
      if (oldVal !== newVal) {
        changes.push({ property: prop, oldValue: oldVal, newValue: newVal });
      }
    }

    // Compare configuration with human-readable output
    const configChanges = compareConfiguration(orig.configuration, updatedField.configuration);
    changes.push(...configChanges);

    diffs.push({
      field: updatedField,
      originalField: orig,
      status: changes.length > 0 ? 'modified' : 'unchanged',
      changes,
    });
  }

  for (const orig of original) {
    if (!matchedOriginalKeys.has(orig.key)) {
      diffs.push({ field: orig, status: 'removed', changes: [] });
    }
  }

  return diffs;
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Container = styled.div`
  margin-top: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const SummaryBar = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
  align-items: center;
`;

const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: ${p => p.theme.typography.weights.semiBold};
  background: ${p => p.$color}15;
  color: ${p => p.$color};
`;

const UnchangedToggle = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  cursor: pointer;
  padding: 3px 0;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${p => p.theme.colors.text};
  }
`;

const DiffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
  max-height: 260px;
  overflow-y: auto;
`;

const DiffCard = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p =>
    p.$status === 'added' ? p.theme.colors.success + '30' :
    p.$status === 'removed' ? p.theme.colors.danger + '30' :
    p.$status === 'modified' ? p.theme.colors.warning + '30' :
    p.theme.colors.border};
  background: ${p =>
    p.$status === 'added' ? p.theme.colors.success + '06' :
    p.$status === 'removed' ? p.theme.colors.danger + '06' :
    p.$status === 'modified' ? p.theme.colors.warning + '06' :
    p.theme.colors.backgroundSecondary};
`;

const DiffCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const DiffFieldLabel = styled.span`
  font-size: 13px;
  font-weight: ${p => p.theme.typography.weights.semiBold};
  color: ${p => p.theme.colors.text};
`;

const TypeBadge = styled.span`
  font-size: 10px;
  padding: 1px 6px;
  background: ${p => p.theme.colors.primary}15;
  color: ${p => p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-weight: 600;
  text-transform: uppercase;
`;

const StatusIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${p => p.$color};
  flex-shrink: 0;
`;

const ChangesList = styled.div`
  margin-top: 4px;
  padding-left: 20px;
`;

const ChangeItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text};
`;

const ChangeProp = styled.span`
  font-weight: 500;
  color: ${p => p.theme.colors.muted};
  min-width: 65px;
  flex-shrink: 0;
`;

const OldValue = styled.span`
  text-decoration: line-through;
  color: ${p => p.theme.colors.danger};
  word-break: break-word;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NewValue = styled.span`
  color: ${p => p.theme.colors.success};
  word-break: break-word;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled.span`
  color: ${p => p.theme.colors.muted};
  flex-shrink: 0;
  font-size: 11px;
`;

// ─── Component ──────────────────────────────────────────────────────────────

const FieldDiffView = ({ originalFields, updatedFields }) => {
  const theme = useTheme();
  const [showUnchanged, setShowUnchanged] = useState(false);

  const diffs = useMemo(
    () => computeFieldDiffs(originalFields, updatedFields),
    [originalFields, updatedFields]
  );

  const modified = diffs.filter(d => d.status === 'modified');
  const added = diffs.filter(d => d.status === 'added');
  const removed = diffs.filter(d => d.status === 'removed');
  const unchanged = diffs.filter(d => d.status === 'unchanged');
  const hasChanges = modified.length > 0 || added.length > 0 || removed.length > 0;

  if (!hasChanges) {
    return (
      <Container theme={theme}>
        <SummaryBadge theme={theme} $color={theme.colors.muted}>
          No changes detected
        </SummaryBadge>
      </Container>
    );
  }

  const visibleDiffs = diffs.filter(d => showUnchanged || d.status !== 'unchanged');

  const statusIcon = (status) => {
    if (status === 'added') return <Plus size={12} />;
    if (status === 'removed') return <X size={12} />;
    if (status === 'modified') return <Pencil size={12} />;
    return <Check size={12} />;
  };

  const statusColor = (status) => {
    if (status === 'added') return theme.colors.success;
    if (status === 'removed') return theme.colors.danger;
    if (status === 'modified') return theme.colors.warning;
    return theme.colors.muted;
  };

  return (
    <Container theme={theme}>
      <SummaryBar theme={theme}>
        {modified.length > 0 && (
          <SummaryBadge theme={theme} $color={theme.colors.warning}>
            {modified.length} modified
          </SummaryBadge>
        )}
        {added.length > 0 && (
          <SummaryBadge theme={theme} $color={theme.colors.success}>
            {added.length} added
          </SummaryBadge>
        )}
        {removed.length > 0 && (
          <SummaryBadge theme={theme} $color={theme.colors.danger}>
            {removed.length} removed
          </SummaryBadge>
        )}
        {unchanged.length > 0 && (
          <UnchangedToggle theme={theme} onClick={() => setShowUnchanged(v => !v)}>
            {showUnchanged ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {unchanged.length} unchanged
          </UnchangedToggle>
        )}
      </SummaryBar>

      <DiffList theme={theme}>
        {visibleDiffs.map((diff, i) => (
          <DiffCard key={diff.field.key || i} theme={theme} $status={diff.status}>
            <DiffCardHeader theme={theme}>
              <StatusIcon $color={statusColor(diff.status)}>
                {statusIcon(diff.status)}
              </StatusIcon>
              <DiffFieldLabel theme={theme}>{diff.field.title}</DiffFieldLabel>
              <TypeBadge theme={theme}>{diff.field.type}</TypeBadge>
            </DiffCardHeader>

            {diff.changes.length > 0 && (
              <ChangesList theme={theme}>
                {diff.changes.map((change, j) => (
                  <ChangeItem key={j} theme={theme}>
                    <ChangeProp theme={theme}>{change.property}:</ChangeProp>
                    {change.oldValue && <OldValue theme={theme} title={change.oldValue}>{change.oldValue}</OldValue>}
                    <Arrow theme={theme}>&rarr;</Arrow>
                    <NewValue theme={theme} title={change.newValue}>{change.newValue || '(empty)'}</NewValue>
                  </ChangeItem>
                ))}
              </ChangesList>
            )}
          </DiffCard>
        ))}
      </DiffList>
    </Container>
  );
};

export default FieldDiffView;
